import { logEvent, setUserProperties, setUserId } from 'firebase/analytics';
import { analytics } from './firebase';
import type { User } from 'firebase/auth';

const ANONYMOUS_ID_KEY = 'choreographer_custom_anonymous_id';
const ANALYTICS_CONSENT_KEY = 'choreographer_analytics_consent';
const INVITATION_CODE_KEY = 'choreographer_invitation_code';

/**
 * Check if the user has consented to analytics tracking.
 * Defaults to Opt-In (false) for EU timezones and Opt-Out (true) for non-EU timezones.
 */
export function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  const stored = localStorage.getItem(ANALYTICS_CONSENT_KEY);
  if (stored !== null) {
    return stored === 'true';
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const isEU = !!tz && (tz.startsWith('Europe/') || tz.startsWith('Atlantic/Reykjavik'));
    return !isEU;
  } catch (e) {
    return false;
  }
}

/**
 * Set the user's analytics consent preference.
 */
export function setAnalyticsConsent(consent: boolean): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(ANALYTICS_CONSENT_KEY, String(consent));
  }
}

/**
 * Generate a standard UUIDv4.
 * Uses crypto.randomUUID if available, with a fallback for older environments.
 */
function generateUUID(): string {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Retrieve or generate a persistent unique anonymous user ID.
 */
export function getOrCreateAnonymousId(): string {
  if (typeof window === 'undefined') return '';
  let anonId = localStorage.getItem(ANONYMOUS_ID_KEY);
  if (!anonId) {
    anonId = generateUUID();
    localStorage.setItem(ANONYMOUS_ID_KEY, anonId);
  }
  return anonId;
}

/**
 * Clean parameters to ensure no PII (Personally Identifiable Information)
 * is accidentally sent to Firebase Analytics.
 */
function cleanParams(params?: Record<string, any>): Record<string, any> {
  if (!params) return {};
  const cleaned: Record<string, any> = {};
  const piiKeys = ['email', 'name', 'password', 'phone', 'address', 'token', 'auth', 'secret'];

  for (const [key, val] of Object.entries(params)) {
    const lowerKey = key.toLowerCase();
    const isPii = piiKeys.some(pii => lowerKey.includes(pii));
    if (!isPii) {
      cleaned[key] = val;
    }
  }
  return cleaned;
}

/**
 * Initialize analytics, setting up user properties and User ID if authenticated.
 */
export function initAnalytics(user?: User | null): void {
  try {
    if (!analytics || !hasAnalyticsConsent()) return;

    const anonymousId = getOrCreateAnonymousId();
    const invitationCode = localStorage.getItem(INVITATION_CODE_KEY);

    const userProperties: Record<string, string> = {
      custom_anonymous_id: anonymousId,
    };
    if (invitationCode) {
      userProperties.invitation_code = invitationCode;
    }

    setUserProperties(analytics, userProperties);

    if (user && user.uid) {
      setUserId(analytics, user.uid);
    } else {
      setUserId(analytics, null);
    }
  } catch (err) {
    console.error('Failed to initialize analytics properties:', err);
  }
}

/**
 * Robust wrapper around logEvent to prevent blocking and filter PII.
 */
export function trackEvent(eventName: string, params?: Record<string, any>): void {
  try {
    if (!hasAnalyticsConsent()) return;

    const invitationCode = typeof window !== 'undefined' ? localStorage.getItem(INVITATION_CODE_KEY) : null;
    const enrichedParams = {
      ...params,
      ...(invitationCode ? { invitation_code: invitationCode } : {}),
    };

    if (!analytics) {
      // In development or if ad-blocked/not supported, output to console for verification
      if (import.meta.env.DEV) {
        console.log(`[Analytics DevLog] Event: ${eventName}`, cleanParams(enrichedParams));
      }
      return;
    }

    logEvent(analytics, eventName, cleanParams(enrichedParams));
  } catch (err) {
    console.error(`Failed to log analytics event: ${eventName}`, err);
  }
}

// --- Category Specific Trackers ---

export function trackTutorialEvent(
  action: 'started' | 'step_finished' | 'finished' | 'tooltip_prompt_start' | 'tooltip_prompt_skip',
  params: { name: string; step?: number; [key: string]: any }
): void {
  trackEvent(`tutorial_${action}`, {
    tutorial_name: params.name,
    step_number: params.step,
    ...params,
  });
}

export function trackSequenceEvent(
  action: 'saved' | 'loaded' | 'folder_created' | 'folder_deleted' | 'folder_renamed' | 'folder_moved' | 'deleted' | 'renamed' | 'moved',
  params?: Record<string, any>
): void {
  trackEvent(`sequence_${action}`, params);
}

export function trackPoseEvent(
  action: 'saved' | 'loaded' | 'applied_to_frame' | 'folder_created' | 'folder_deleted' | 'folder_renamed' | 'folder_moved' | 'deleted' | 'renamed' | 'moved',
  params?: Record<string, any>
): void {
  trackEvent(`pose_${action}`, params);
}

export function trackTimelineEvent(
  action: 'pose_saved_to_keyframe' | 'keyframe_moved' | 'keyframe_copied' | 'keyframe_deleted' | 'keyframe_inserted' | 'settings_changed' | 'playback_action' | 'zoom_action' | 'compact_view_toggled' | 'erased' | 'dice_pose_created' | 'soundtrack_loaded' | 'autosave_toggled',
  params?: Record<string, any>
): void {
  trackEvent(`timeline_${action}`, params);
}

export function trackGeneralEvent(
  action: 'signed_in' | 'signed_out' | 'showcase_opened' | 'user_manual_opened' | 'tou_opened' | 'privacy_opened' | 'plans_opened' | 'sign_in_failed' | 'shared_soundtracks_opened',
  params?: Record<string, any>
): void {
  trackEvent(`general_${action}`, params);
}

export function trackEditorEvent(
  action: 'completed_pose_action' | 'undo_used' | 'redo_used' | 'pose_reset' | 'joint_reset' | 'camera_reset' | 'mirror_mode_toggled' | 'mirror_pose_flipped' | 'joint_reflected' | 'labels_toggled' | 'axes_toggled' | 'confirm_reset_toggled' | 'joint_selected_3d' | 'joint_context_action' | 'camera_adjusted' | 'keyboard_pose_action' | 'marker_selected_3d' | 'marker_snapped' | 'marker_deleted' | 'marker_placed' | 'markers_cleared' | 'direct_drag_pose_action',
  params?: Record<string, any>
): void {
  trackEvent(`editor_${action}`, params);
}

export function trackProfileEvent(
  action: 'opened' | 'nickname_updated',
  params?: Record<string, any>
): void {
  trackEvent(`profile_${action}`, params);
}

export function trackShareEvent(
  action: 'dialog_opened' | 'email_added' | 'email_removed' | 'permissions_saved' | 'folder_refreshed' | 'unshared',
  params?: Record<string, any>
): void {
  trackEvent(`share_${action}`, params);
}
